/* ============================================================
   Hero Parallax — 圖層視差輪播引擎
   單一浮點 pos 驅動全部圖層;requestAnimationFrame 推進;
   easeInOutCubic 補間。所有變換以 --tx(px)/--sc/--op 交給 CSS 組成。
   自訂元素 <hero-parallax>,每個實例自行初始化。
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('hero-parallax')) return;

  // 各圖層的視差/縮放/透明度預設(規格內建值)
  var LAYER = {
    bg2: { parallax: 0.3 }, // 第二背景層(透明 PNG):比主體慢,讀起來在更後方
    subject: { parallax: 0.42, falloff: 0.4 },
    eyebrow: { parallax: 0.9 },
    title: { parallax: 0.9 },
    meta: { parallax: 0.9 },
    desc: { parallax: 0.6 },
    cta: { parallax: 0.6 }
  };

  var easeInOutCubic = function (t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };
  var clamp = function (v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  };
  var lerp = function (a, b, t) {
    return a + (b - a) * t;
  };

  class HeroParallax extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.stage = this.querySelector('.hpx-stage');
      this.slideEls = Array.prototype.slice.call(this.querySelectorAll('.hpx-slide'));
      this.count = this.slideEls.length;

      // 設定(來自 data-*)
      this.loop = this.dataset.loop === 'true' && this.count > 1;
      this.intensity = parseFloat(this.dataset.parallax);
      if (isNaN(this.intensity)) this.intensity = 1;
      this.transitionMs = parseInt(this.dataset.transition, 10) || 850;
      this.autoplayMs = parseInt(this.dataset.interval, 10) || 0;
      this.indicatorType = this.dataset.indicator || 'ring';
      this.subjectSize = parseFloat(this.dataset.subjectSize);
      if (isNaN(this.subjectSize)) this.subjectSize = 50;

      this.reduced =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // 狀態
      this.pos = 0; // 連續浮點位置
      this.anim = null; // 補間動畫
      this.rafId = null;
      this.dragging = false;

      if (this.count === 0) return;

      // 預算每張投影片各圖層,快取要動的 DOM
      this.slides = this.slideEls.map(this._collectLayers.bind(this));

      this._buildIndicator();
      this._bindEvents();

      // 量測主體正方形邊長(= 舞台高度 × subjectSize%),寫入 --hpx-subject-size
      this._measure();

      // 首次渲染
      this._render();
      this._startAutoplay();

      // 版面穩定後再量一次,避免樣式尚未套用時 clientHeight 為 0
      var self0 = this;
      requestAnimationFrame(function () {
        self0._measure();
        self0._render();
      });
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    /* ---------- 收集單張投影片的可動圖層 ---------- */
    _collectLayers(slideEl) {
      var layers = [];
      var add = function (el, key) {
        if (el) layers.push({ el: el, cfg: LAYER[key] });
      };
      // 背景(只做交叉淡化,無視差)
      var bg = slideEl.querySelector('.hpx-bg');
      // 主體
      add(slideEl.querySelector('.hpx-subject'), 'subject');
      // 文字層(含 behind 標題)
      slideEl.querySelectorAll('[data-layer]').forEach(function (el) {
        add(el, el.dataset.layer);
      });
      return { el: slideEl, bg: bg, layers: layers };
    }

    /* ---------- 量測主體正方形邊長(= 舞台高度 × subjectSize%) ---------- */
    _measure() {
      if (!this.stage) return;
      var h = this.stage.clientHeight || 0;
      if (!h) return;
      var px = h * (this.subjectSize / 100);
      this.style.setProperty('--hpx-subject-size', px.toFixed(1) + 'px');
    }

    /* ---------- 換頁的目標索引 ---------- */
    _wrap(offset) {
      // loop 時把 offset 收進 [-count/2, count/2),讓兩側都能滑入
      if (!this.loop) return offset;
      var n = this.count;
      while (offset > n / 2) offset -= n;
      while (offset < -n / 2) offset += n;
      return offset;
    }

    /* ---------- 渲染:依 pos 更新所有圖層 ---------- */
    _render() {
      var stageW = this.stage.clientWidth || 1;
      var k = this.intensity;

      for (var i = 0; i < this.count; i++) {
        var s = this.slides[i];
        var offset = this._wrap(i - this.pos);
        var abs = Math.abs(offset);

        // 背景:交叉淡化,不位移
        if (s.bg) {
          s.bg.style.setProperty('--op', 1 - clamp(abs, 0, 1));
        }

        // 其餘圖層:位移 + 透明度(+ 主體縮放)
        for (var j = 0; j < s.layers.length; j++) {
          var L = s.layers[j];
          var isSubject = L.el.classList.contains('hpx-subject');
          var isBg2 = L.el.classList.contains('hpx-bg2');

          if (isSubject) {
            // 主體:相鄰張中心固定落在舞台左右緣 → 各露一半(固定半步,不隨視差強度)
            var txS = offset * stageW * 0.5;
            L.el.style.setProperty('--tx', txS.toFixed(2) + 'px');
            var sc = clamp(1 - L.cfg.falloff * abs, 0.6, 1);
            // 相鄰(abs≤1)保留可見度當作探頭卡片;abs≥2 完全淡出
            var peek = 0.6;
            var op = abs <= 1 ? 1 - (1 - peek) * abs : peek * (2 - abs);
            L.el.style.setProperty('--sc', sc.toFixed(3));
            L.el.style.setProperty('--op', clamp(op, 0, 1).toFixed(3));
          } else if (isBg2) {
            // 第二背景層:與底層背景相同的柔和交叉淡化(不像文字層加速淡出)
            var txB = offset * stageW * L.cfg.parallax * k;
            L.el.style.setProperty('--tx', txB.toFixed(2) + 'px');
            L.el.style.setProperty('--op', (1 - clamp(abs, 0, 1)).toFixed(3));
          } else {
            var tx = offset * stageW * L.cfg.parallax * k;
            L.el.style.setProperty('--tx', tx.toFixed(2) + 'px');
            L.el.style.setProperty('--op', clamp(1 - 1.5 * abs, 0, 1).toFixed(3));
          }
        }

        // 只有中心投影片可互動(CTA 可點)
        s.el.style.pointerEvents = abs < 0.5 ? '' : 'none';
        // 不切換 slide 的 z-index:讓所有主體(z2)一律疊在所有背景(z0/1)之上,
        // 相鄰主體才能露出於當前投影片背景之前(否則會被不透明背景蓋住而看不見探頭)。
      }

      this._renderIndicator();
    }

    /* ---------- Indicator ---------- */
    _buildIndicator() {
      this.indicator = this.querySelector('.hpx-indicator');
      if (!this.indicator || this.indicatorType === 'none' || this.count < 2) {
        if (this.indicator) this.indicator.hidden = true;
        return;
      }

      if (this.indicatorType === 'dots') {
        var dots = document.createElement('div');
        dots.className = 'hpx-dots';
        this.dotBtns = [];
        for (var d = 0; d < this.count; d++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'hpx-dots__dot';
          b.setAttribute('aria-label', '第 ' + (d + 1) + ' 張');
          b.dataset.index = d;
          dots.appendChild(b);
          this.dotBtns.push(b);
        }
        this.indicator.appendChild(dots);
        return;
      }

      // ring:虛線軌道 + N 個節點 + 空心圓環
      var track = document.createElement('div');
      track.className = 'hpx-track';

      // 首尾各留約 12% 邊距(同時給虛線當起訖,兩邊數字保持一致)
      var pad = 0.12;
      track.style.setProperty('--hpx-pad', (pad * 100) + '%');

      var line = document.createElement('div');
      line.className = 'hpx-track__line';
      track.appendChild(line);

      this.nodePcts = [];
      this.nodeBtns = [];
      this.nodeDots = [];
      for (var n = 0; n < this.count; n++) {
        var pct = this.count === 1 ? 0.5 : pad + (1 - 2 * pad) * (n / (this.count - 1));
        this.nodePcts.push(pct);

        var node = document.createElement('button');
        node.type = 'button';
        node.className = 'hpx-node';
        node.style.left = (pct * 100) + '%';
        node.setAttribute('aria-label', '第 ' + (n + 1) + ' 張');
        node.dataset.index = n;
        var dot = document.createElement('span');
        dot.className = 'hpx-node__dot';
        node.appendChild(dot);
        track.appendChild(node);
        this.nodeBtns.push(node);
        this.nodeDots.push(dot);
      }

      var ring = document.createElement('div');
      ring.className = 'hpx-ring';
      ring.setAttribute('role', 'slider');
      ring.setAttribute('aria-label', '拖曳切換投影片');
      var fill = document.createElement('span');
      fill.className = 'hpx-ring__fill';
      ring.appendChild(fill);
      track.appendChild(ring);

      this.indicator.appendChild(track);
      this.track = track;
      this.ring = ring;
    }

    _renderIndicator() {
      if (this.indicatorType === 'dots' && this.dotBtns) {
        var active = ((Math.round(this.pos) % this.count) + this.count) % this.count;
        for (var i = 0; i < this.dotBtns.length; i++) {
          this.dotBtns[i].classList.toggle('is-active', i === active);
        }
        return;
      }
      if (this.indicatorType !== 'ring' || !this.track) return;

      var trackW = this.track.clientWidth || 1;
      var pos = this.pos;
      var lo = Math.floor(pos);
      var hi = Math.ceil(pos);
      var frac = pos - lo;

      // 沿軌道連續插值 ring 位置(節點索引取模,支援 loop 回捲)
      var n = this.count;
      var pctLo = this.nodePcts[((lo % n) + n) % n];
      var pctHi = this.nodePcts[((hi % n) + n) % n];
      var ringPct = lerp(pctLo, pctHi, frac);
      this.ring.style.setProperty('--ring-x', (ringPct * trackW).toFixed(2) + 'px');

      // 到站才填實
      var dist = Math.abs(pos - Math.round(pos));
      var fill = dist < 0.09 ? 1 - dist / 0.09 : 0;
      this.ring.style.setProperty('--fill', fill.toFixed(3));

      // 被圓環罩住的當前節點隱藏自身圓點,改由圈內填實點呈現
      var nearest = ((Math.round(pos) % n) + n) % n;
      for (var d = 0; d < this.nodeDots.length; d++) {
        this.nodeDots[d].style.opacity = d === nearest ? (1 - fill).toFixed(3) : '1';
      }
    }

    /* ---------- 換頁補間 ---------- */
    goTo(target, opts) {
      opts = opts || {};
      if (!this.loop) target = clamp(target, 0, this.count - 1);

      var dur = opts.instant || this.reduced ? 0 : this.transitionMs;
      if (dur === 0) {
        this.pos = this._normalize(target);
        this._render();
        return;
      }
      this.anim = { from: this.pos, to: target, start: null, dur: dur };
      this._tick();
    }

    _normalize(pos) {
      if (!this.loop) return clamp(pos, 0, this.count - 1);
      var n = this.count;
      return ((pos % n) + n) % n;
    }

    _tick() {
      var self = this;
      if (this.rafId) cancelAnimationFrame(this.rafId);
      var step = function (ts) {
        var a = self.anim;
        if (!a) return;
        if (a.start === null) a.start = ts;
        var t = a.dur === 0 ? 1 : clamp((ts - a.start) / a.dur, 0, 1);
        self.pos = a.from + (a.to - a.from) * easeInOutCubic(t);
        self._render();
        if (t < 1) {
          self.rafId = requestAnimationFrame(step);
        } else {
          // 到站後把 pos 正規化,loop 時無縫(圖層用 wrap offset)
          self.pos = self._normalize(a.to);
          self.anim = null;
          self._render();
        }
      };
      this.rafId = requestAnimationFrame(step);
    }

    next() {
      this.goTo(Math.round(this.pos) + 1);
    }
    prev() {
      this.goTo(Math.round(this.pos) - 1);
    }

    /* ---------- 事件 ---------- */
    _bindEvents() {
      var self = this;

      // 箭頭
      this.prevBtn = this.querySelector('.hpx-arrow--prev');
      this.nextBtn = this.querySelector('.hpx-arrow--next');
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self._userJump(function () { self.prev(); }); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self._userJump(function () { self.next(); }); });

      // 節點 / 圓點 跳頁(事件委派)
      if (this.indicator) {
        this.indicator.addEventListener('click', function (e) {
          var btn = e.target.closest('.hpx-node, .hpx-dots__dot');
          if (!btn) return;
          self._userJump(function () { self.goTo(parseInt(btn.dataset.index, 10)); });
        });
      }

      // 鍵盤 ←/→
      this.stage.setAttribute('tabindex', '0');
      this.stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self._userJump(function () { self.prev(); }); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); self._userJump(function () { self.next(); }); }
      });

      // hover / focus 暫停自動輪播
      this.stage.addEventListener('mouseenter', function () { self._pauseAutoplay(); });
      this.stage.addEventListener('mouseleave', function () { if (!self.dragging) self._startAutoplay(); });
      this.stage.addEventListener('focusin', function () { self._pauseAutoplay(); });
      this.stage.addEventListener('focusout', function () { self._startAutoplay(); });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) self._pauseAutoplay(); else self._startAutoplay();
      });

      // 舞台觸控/拖曳 swipe
      this._bindStageDrag();
      // 圓環 scrub
      if (this.ring) this._bindRingScrub();

      // resize 重算(主體尺寸依舞台高度、px 位移都要重新量測與渲染)
      this._onResize = function () { self._measure(); self._render(); };
      window.addEventListener('resize', this._onResize);
    }

    _userJump(fn) {
      this._pauseAutoplay();
      fn();
      this._restartAutoplaySoon();
    }

    /* --- 舞台拖曳:更新 pos,放手吸附最近 --- */
    _bindStageDrag() {
      var self = this;
      var startX = 0, startPos = 0, active = false, moved = false;

      var down = function (e) {
        if (self.count < 2) return;
        if (e.target.closest('.hpx-arrow, .hpx-ring, .hpx-node, .hpx-cta, a')) return;
        active = true; moved = false;
        startX = e.clientX;
        startPos = self.pos;
        self.dragging = true;
        self._pauseAutoplay();
        if (self.anim) { self.anim = null; if (self.rafId) cancelAnimationFrame(self.rafId); }
        self.stage.classList.add('is-dragging');
        self.stage.setPointerCapture && self.stage.setPointerCapture(e.pointerId);
      };
      var move = function (e) {
        if (!active) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        var w = self.stage.clientWidth || 1;
        var next = startPos - dx / w; // 往左拖 = 前進
        if (!self.loop) next = clamp(next, 0, self.count - 1);
        self.pos = next;
        self._render();
      };
      var up = function () {
        if (!active) return;
        active = false;
        self.dragging = false;
        self.stage.classList.remove('is-dragging');
        if (moved) self.goTo(Math.round(self.pos));
        self._restartAutoplaySoon();
      };

      this.stage.addEventListener('pointerdown', down);
      this.stage.addEventListener('pointermove', move);
      this.stage.addEventListener('pointerup', up);
      this.stage.addEventListener('pointercancel', up);
    }

    /* --- 圓環 scrub:拖曳即時更新 pos,放手吸附最近節點 --- */
    _bindRingScrub() {
      var self = this;
      var active = false;

      var toPos = function (clientX) {
        var rect = self.track.getBoundingClientRect();
        var pct = clamp((clientX - rect.left) / (rect.width || 1), 0, 1);
        // 反解:pct → pos(節點等距,含 12% 邊距)
        var pad = 0.12;
        var span = 1 - 2 * pad;
        var p = span === 0 ? 0 : ((pct - pad) / span) * (self.count - 1);
        return clamp(p, 0, self.count - 1);
      };

      var down = function (e) {
        active = true;
        e.preventDefault();
        e.stopPropagation();
        self.dragging = true;
        self._pauseAutoplay();
        if (self.anim) { self.anim = null; if (self.rafId) cancelAnimationFrame(self.rafId); }
        self.ring.classList.add('is-dragging');
        self.ring.setPointerCapture && self.ring.setPointerCapture(e.pointerId);
        self.pos = toPos(e.clientX);
        self._render();
      };
      var move = function (e) {
        if (!active) return;
        self.pos = toPos(e.clientX);
        self._render();
      };
      var up = function () {
        if (!active) return;
        active = false;
        self.dragging = false;
        self.ring.classList.remove('is-dragging');
        self.goTo(Math.round(self.pos)); // 吸附最近節點
        self._restartAutoplaySoon();
      };

      this.ring.addEventListener('pointerdown', down);
      this.ring.addEventListener('pointermove', move);
      this.ring.addEventListener('pointerup', up);
      this.ring.addEventListener('pointercancel', up);
    }

    /* ---------- 自動輪播 ---------- */
    _startAutoplay() {
      if (this.reduced || !this.autoplayMs || this.count < 2 || this.dragging) return;
      this._stopAutoplay();
      var self = this;
      this.timer = setInterval(function () { self.next(); }, this.autoplayMs);
    }
    _pauseAutoplay() {
      this._stopAutoplay();
    }
    _stopAutoplay() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }
    _restartAutoplaySoon() {
      var self = this;
      this._stopAutoplay();
      if (this._resume) clearTimeout(this._resume);
      this._resume = setTimeout(function () { self._startAutoplay(); }, 600);
    }
  }

  customElements.define('hero-parallax', HeroParallax);
})();
